import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { DataService } from '../services';
import * as CalculationActions from './calculation-actions';
import * as HeaderActions from './actions/header-line-actions';
import { Router } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { AppState } from './app-state';
import { CalculationState } from './calculation-state';
import { undoRedo } from '../app.module';
import { selectCalculationMeta, selectCalculationState, selectCreatingIds, selectLines, selectStaleIds, selectTail } from './calculation-selectors';
import { DiffType, StaleProperty } from './undo-redo';
import { createViewModel } from './functions/convert-calculation-line';
import { PdfExportService } from '../features/pdf-export/services/pdf-service/pdf-export.service';
import { ProjectCalculationService } from '../services/project-calculation/project-calculation.service';
import saveAs from 'file-saver';

declare type EntityAction = {
    action: DiffType;
    entities: any
}

declare type UndoActions = {
    [K in keyof Partial<CalculationState>]: EntityAction[]
}

@Injectable()
export class CalculationEffects {

    handleError$ = createEffect(() =>
        this.actions$.pipe(
            filter(CalculationActions.isErrorAction), // Filter for error actions
            tap((action) => {
                // console.log("ERROR")
                // Handle the error, e.g., log it or show a notification
                // console.error('Error occurred:', action.error);
                // Optionally, dispatch another action
            })
        ),
        { dispatch: false } // Set to false if you are not dispatching a new action
    );

    // Handle load calculation
    loadCalculation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.loadCalculation),
            mergeMap((action) =>
                this.calculationService.loadRawCalculation(action.projectId, action.calculationId, action.version).pipe(
                    map(calculation => CalculationActions.loadCalculationSuccess({ calculation })),
                    tap(() => {
                        // Show calculation page
                        this.router.navigate(['project', action.projectId, 'calc', action.calculationId]);
                    }),
                    catchError(error => of(CalculationActions.loadCalculationFailure({ error })))
                )
            )
        )
    );

    loadRefCalculation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.loadRefCalculation),
            mergeMap((action) =>
                this.calculationService.loadRawCalculation(action.projectId, action.calculationId, action.version).pipe(
                    map(calculation => CalculationActions.loadRefCalculationSuccess({ calculation })),
                    catchError(error => of(CalculationActions.loadRefCalculationFailure({ error })))
                )
            )
        )
    );

    // Handle create calculation
    createCalculation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.createCalculation),
            mergeMap((action) =>
                this.calculationService.createCalculation(action.project, action.calculationType).pipe(
                    map((calculation) =>
                        CalculationActions.loadCalculation({
                            projectId: action.project.id,
                            calculationId: calculation.meta.id
                        })
                    ),
                    tap(() => {
                        // Refresh the calculation list in the project overview
                        this.projectCalculationService.refreshCalculations();
                    }),
                    catchError(error =>
                        of(CalculationActions.createCalculationFailure({ error }))
                    )
                )
            )
        )
    );

    updateLineUi$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateLineUi),
            withLatestFrom(this.store.select(selectCreatingIds)),
            filter(([{ line }, creatingIds]) => !creatingIds[line.id]), // Only proceed if the line is not in the creatingIds list
            mergeMap(([{ projectId, calculationId, line }]) =>
                this.calculationService.updateLine$(projectId, calculationId, line).pipe(
                    map(() => CalculationActions.updateLineSuccess({ line })),
                    catchError(error =>
                        of(CalculationActions.updateLineFailure({ error }))
                    )
                )
            )
        )
    );

    updateLineStale$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateCalculationLineStale),
            mergeMap(({ projectId, calculationId, line }) =>
                this.calculationService.updateLine$(projectId, calculationId, line).pipe(
                    map(() => CalculationActions.updateLineSuccess({ line })),
                    catchError(error =>
                        of(CalculationActions.updateLineFailure({ error }))
                    )
                )
            )
        )
    );

    /**
     * Check if a created line is stale. That happens if the line was updated before creation completed
     * This scenario can happen when sortOrders are rearranged in the UI and the backend is slow to respond
     * Especially when multiple lines are created shortly after each other
     *
     * @memberof CalculationEffects
     */
    checkForStaleCalculationValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.insertLineSuccess),
            withLatestFrom(
                this.store.select(selectStaleIds),
                this.store.select(selectCalculationMeta),
                // Get the updated lines from the store
                // This is necessary because the line in the action might be stale itself
                this.store.select(selectLines)
            ),
            // tap(([{ line }, staleIds]) => {
            //     console.log('[calculation-effects] Check staleness', line, staleIds);
            // }),
            // Check if the line is stale
            filter(([{ line }, staleIds]) => !!staleIds[line.id]),
            // tap(([{ line }, staleIds]) => {
            //     console.log('[calculation-effects] Stale line detected', line, staleIds[line.id]);
            // }),
            map(([{ line }, staleIds, meta, lines]) =>
                CalculationActions.updateCalculationLineStale({
                    calculationId: meta?.id ?? '',
                    projectId: meta?.projectId ?? '',
                    line: lines.find(l => l.id === line.id) ?? line // Find the line in the lines array to ensure we have the latest version
                })
            )
        )
    );

    insertLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.insertLine),
            mergeMap(({ projectId, calculationId, line }) =>
                this.calculationService.createLine$(projectId, calculationId, line).pipe(
                    map(() => CalculationActions.insertLineSuccess({ line })),
                    catchError(error =>
                        of(CalculationActions.insertLineFailure({ error }))
                    )
                )
            )
        )
    );

    deleteLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.deleteLine),
            withLatestFrom(this.store.select(selectCreatingIds)),
            filter(([{ line }, creatingIds]) => !creatingIds[line.id]), // Only proceed if the line is not in the creatingIds list
            mergeMap(([action]) =>
                // Copy the calculation to another calculation under the same project
                this.calculationService.deleteLine$(action.projectId, action.calculationId, action.line).pipe(
                    map((line) =>
                        CalculationActions.deleteLineSuccess({ line: action.line }) // Return the updated line
                    ),
                    catchError(error =>
                        of(CalculationActions.deleteLineFailure({ error }))
                    )
                )

            )
        )
    );

    deleteLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.deleteLines),
            mergeMap((action) =>
                // Use forkJoin to handle multiple delete operations in parallel
                forkJoin(action.lines.map((line) =>
                    this.calculationService.deleteLine$(action.projectId, action.calculationId, line)
                )).pipe(
                    map((results) =>
                        CalculationActions.deleteLinesSuccess({ lines: action.lines }) // Return the updated lines
                    ),
                    catchError(error =>
                        of(CalculationActions.deleteLinesFailure({ error }))
                    )
                )
            )
        )
    );

    deleteTailLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.deleteTailLines),
            mergeMap((action) =>
                // Use forkJoin to handle multiple delete operations in parallel
                forkJoin(action.lines.map((line) =>
                    this.calculationService.deleteTailLine$(action.projectId, action.calculationId, line)
                )).pipe(
                    map((results) =>
                        CalculationActions.deleteTailLinesSuccess({ lines: action.lines }) // Return the updated lines
                    ),
                    catchError(error =>
                        of(CalculationActions.deleteTailLinesFailure({ error }))
                    )
                )
            )
        )
    );

    updateTailLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateTailLineUi),
            withLatestFrom(this.store.select(selectCreatingIds)),
            filter(([{ line }, creatingIds]) => !creatingIds[line.id]), // Only proceed if the line is not in the creatingIds list
            mergeMap(([action]) =>
                // Copy the calculation to another calculation under the same project
                this.calculationService.updateTailLine$(action.projectId, action.calculationId, action.line).pipe(
                    map((line) =>
                        CalculationActions.updateTailLineSuccess({ line: action.line }) // Return the updated line
                    ),
                    catchError(error =>
                        of(CalculationActions.updateTailLineFailure({ error }))
                    )
                )

            )
        )
    );

    insertTailLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.insertTailLine),
            mergeMap(({ projectId, calculationId, line }) =>
                this.calculationService.createTailLine$(projectId, calculationId, line).pipe(
                    map(() => CalculationActions.insertTailLineSuccess({ line })),
                    catchError(error =>
                        of(CalculationActions.insertTailLineFailure({ error }))
                    )
                )
            )
        )
    );

    bulkInsertTailLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.bulkInsertTailLines),
            withLatestFrom(this.store.select(selectCalculationMeta)),
            mergeMap(([{ lines }, meta]) =>
                this.calculationService.bulkAddTailLines$(meta?.projectId ?? '', meta?.id ?? '', lines).pipe(
                    map(() => CalculationActions.bulkInsertTailLinesSuccess({ lines })),
                    catchError(error =>
                        of(CalculationActions.bulkInsertTailLinesFailure({ error }))
                    )
                )
            )
        )
    );

    checkForStaleTailValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.insertTailLineSuccess),
            withLatestFrom(
                this.store.select(selectStaleIds),
                this.store.select(selectCalculationMeta),
                // Get the updated lines from the store
                // This is necessary because the line in the action might be stale itself
                this.store.select(selectTail)
            ),
            filter(([{ line }, staleIds]) => !!staleIds[line.id]),
            map(([{ line }, , meta, tail]) =>
                CalculationActions.updateTailLineStale({
                    calculationId: meta?.id ?? '',
                    projectId: meta?.projectId ?? '',
                    line: tail.find(l => l.id === line.id) ?? line // Find the line in the lines array to ensure we have the latest version
                })
            )
        )
    );

    updateTailStale$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateTailLineStale),
            tap(({ line }) => { console.log('Update tail line stale', line); }),
            mergeMap(({ projectId, calculationId, line }) =>
                this.calculationService.updateTailLine$(projectId, calculationId, line).pipe(
                    map(() => CalculationActions.updateTailLineSuccess({ line })),
                    catchError(error =>
                        of(CalculationActions.updateTailLineFailure({ error }))
                    )
                )
            )
        )
    );

    deleteTailLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.deleteTailLine),
            withLatestFrom(this.store.select(selectCreatingIds)),
            filter(([{ line }, creatingIds]) => !creatingIds[line.id]), // Only proceed if the line is not in the creatingIds list
            mergeMap(([action]) =>
                this.calculationService.deleteTailLine$(action.projectId, action.calculationId, action.line).pipe(
                    map((line) =>
                        CalculationActions.deleteTailLineSuccess({ line: action.line })
                    ),
                    catchError(error =>
                        of(CalculationActions.deleteTailLineFailure({ error }))
                    )
                )
            )
        )
    );

    insertLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.insertLines),
            mergeMap(({ projectId, calculationId, lines }) =>
                this.calculationService.bulkAddLines$(projectId, calculationId, lines).pipe(
                    map(() => CalculationActions.insertLinesSuccess({ lines })),
                    catchError(error =>
                        of(CalculationActions.insertLinesFailure({ error }))
                    )
                )
            )
        )
    );

    updateMeta$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateMeta),
            debounceTime(500), // Wait for 500ms after the last emission
            mergeMap((action) =>
                this.calculationService.updateCalculationMeta$(action.projectId, action.calculationId, action.meta).pipe(
                    map(() =>
                        CalculationActions.updateMetaSuccess(action)
                    ),
                    catchError(error =>
                        of(CalculationActions.updateMetaFailure({ error }))
                    )
                )
            )
        )
    );

    updateHeaderLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeaderActions.updateHeaderLine),
            mergeMap((action) =>
                this.calculationService.updateHeaderLine$(action.projectId, action.calculationId, action.line).pipe(
                    map(() =>
                        HeaderActions.updateHeaderLineSuccess(action)
                    ),
                    catchError(error =>
                        of(HeaderActions.updateHeaderLineFailure({ error }))
                    )
                )
            )
        )
    );

    createCalculationVariation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.variationsFromCalculation),
            mergeMap((action) =>
                this.calculationService.createVariationsFromCalculation$(action.projectId, action.calculationId).pipe(
                    tap(() => {
                        // Refresh the calculation list in the project overview
                        this.projectCalculationService.refreshCalculations();
                    }),
                    map((calculation) =>
                        CalculationActions.loadCalculation({
                            projectId: action.projectId,
                            calculationId: calculation.meta.id
                        })
                    ),
                    catchError(error =>
                        of(CalculationActions.variationsFromCalculationFailure({ error }))
                    )
                )
            )
        )
    );

    freezeCalculation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.freezeCalculation),
            mergeMap((action) =>
                this.calculationService.copyCalculationVersion$(action.projectId, action.calculationId, action.version ?? 0).pipe(
                    map((meta) =>
                        CalculationActions.loadCalculation({
                            projectId: meta?.projectId ?? '',
                            calculationId: meta?.id ?? ''
                        })
                    ),
                    tap(() => {
                        // Refresh the calculation list in the project overview
                        this.projectCalculationService.refreshCalculations();
                    }),
                    catchError(error =>
                        of(CalculationActions.freezeCalculationFailure({ error }))
                    )
                )
            )
        )
    );

    unlockCalculation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.unlockCalculation),
            mergeMap((action) =>
                this.calculationService.unlockCalculation$(action.projectId, action.calculationId).pipe(
                    map((unlockResult) =>
                        CalculationActions.loadCalculation({
                            projectId: action.projectId,
                            calculationId: action.calculationId
                        })
                    ),
                    catchError(error =>
                        of(CalculationActions.unlockCalculationFailure({ error }))
                    )
                )
            )
        )
    );

    deleteUndoHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.loadCalculation),
            tap(() => {
                // Empty the undo queue
                undoRedo.past = [];
                undoRedo.future = [];
                undoRedo.queueUpdated$.next();
            })
        ),
        { dispatch: false } // Ensure no action is dispatched
    );

    updateProvisionalCostsSetting = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.updateMetaProvisionalSetting),
            withLatestFrom(this.store.select(selectLines)),
            mergeMap(([action, lines]) => {
                // If set to false, all existing net provisional cost lines have to be converted
                const updateLines = (!action.meta.includeProvisionalPostLinesAfterTail ? lines.filter(l => l.unit === 'sptN') : [])
                    .map(l => ({
                        ...l,
                        unit: 'spt'
                    }));

                // Meta update
                const metaUpdate = this.calculationService.updateCalculationMeta$(action.projectId, action.calculationId, action.meta).pipe(
                    map(res => CalculationActions.updateMetaSuccess({
                        calculationId: action.calculationId,
                        projectId: action.projectId,
                        meta: res
                    })),
                    catchError((error) => of(CalculationActions.updateMetaFailure({ error })))
                );

                if (updateLines) {
                    return forkJoin([
                        metaUpdate,
                        // Also update all lines
                        this.calculationService.bulkUpdateLines$(
                            action.projectId,
                            action.calculationId,
                            updateLines
                        ).pipe(
                            map(() => CalculationActions.bulkUpdateLinesSuccess({
                                lines: updateLines
                            })),
                            catchError((error) => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                        )
                    ]).pipe(
                        mergeMap(([metaUpdateResult, updateLinesResult]) => [
                            metaUpdateResult,
                            updateLinesResult
                        ])
                    )
                } else {
                    return metaUpdate;
                }
            })
        )
    );

    bulkUpdateLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.bulkUpdateLines),
            withLatestFrom(this.store.select(selectCalculationMeta), this.store.select(selectCreatingIds)),
            mergeMap(([action, meta, creatingIds]) => {
                const eligibleLines = action.lines.filter(l => !creatingIds[l.id]);
                return this.calculationService.bulkUpdateLines$(meta?.projectId ?? '', meta?.id ?? '', eligibleLines).pipe(
                    filter(() => !!eligibleLines.length), // Only proceed if there are eligible lines
                    map(() =>
                        CalculationActions.bulkUpdateLinesSuccess({
                            lines: action.lines
                        })
                    ),
                    catchError(error =>
                        of(CalculationActions.bulkUpdateLinesFailure({ error }))
                    )
                )
            }
            )
        )
    );

    bulkUpdateTailLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.bulkUpdateTailLines),
            withLatestFrom(this.store.select(selectCalculationMeta), this.store.select(selectCreatingIds)),
            mergeMap(([action, meta, creatingIds]) => {
                const eligibleLines = action.lines.filter(l => !creatingIds[l.id]);
                return this.calculationService.bulkUpdateTailLines$(meta?.projectId ?? '', meta?.id ?? '', eligibleLines).pipe(
                    filter(() => !!eligibleLines.length), // Only proceed if there are eligible lines
                    map(() =>
                        CalculationActions.bulkUpdateTailLinesSuccess({
                            lines: action.lines
                        })
                    ),
                    catchError(error =>
                        of(CalculationActions.bulkUpdateTailLinesFailure({ error }))
                    )
                )
            }
            )
        )
    );

    undo$ = createEffect(() => this.actions$.pipe(
        ofType(CalculationActions.calculationUndo),
        mergeMap((action) =>
            forkJoin(
                this.convertDiffsToBackendActions(action.diffs, action.reversedState)
            ).pipe(
                mergeMap(results => {
                    // Check if one of the fork join result actions holds error information
                    const errorAction = results.find(result => CalculationActions.isErrorAction(result));

                    if (errorAction) {
                        // Return the error action
                        return of(errorAction);
                    } else {
                        // Success trigger; rebuild front end lines
                        return of(CalculationActions.calculationUndoRedoDone({ reversedState: action.reversedState }));
                    }
                }),
                catchError(error => {
                    // No errors expected here
                    console.error('Unexpected error caught:', error);
                    return of(CalculationActions.calculationUndoRedoFailure({ error: error || 'Unknown error' }));
                })
            )
        ),
        // tap(result => {console.log('CONVERSION RESULT', result)})
    ));

    redo$ = createEffect(() => this.actions$.pipe(
        ofType(CalculationActions.calculationRedo),
        mergeMap((action) =>
            forkJoin(
                this.convertDiffsToBackendActions(action.diffs, action.reversedState, false)
            ).pipe(
                mergeMap(results => {
                    // Check if one of the fork join result actions holds error information
                    const errorAction = results.find(result => CalculationActions.isErrorAction(result));

                    if (errorAction) {
                        // Return the error action
                        return of(errorAction);
                    } else {
                        // Success trigger; rebuild front end lines
                        return of(CalculationActions.calculationUndoRedoDone({ reversedState: action.reversedState }));
                    }
                }),
                catchError(error => {
                    // No errors expected here
                    console.error('Unexpected error caught:', error);
                    return of(CalculationActions.calculationUndoRedoFailure({ error: error || 'Unknown error' }));
                })
            )
        )
    ));

    // Undo helper functions
    private convertDiffsToBackendActions(
        diffs: StaleProperty[],
        reversedState: CalculationState,
        isUndo = true
    ): Observable<Action>[] {
        try {
            const actions = this.groupUndoActions(diffs, reversedState);
            const { projectId = '', id: calculationId = '' } = reversedState.meta ?? {};

            const entityActions: Observable<Action>[] = [];

            for (const entity in actions) {
                switch (entity) {
                    case 'meta':
                        entityActions.push(
                            this.calculationService.updateCalculationMeta$(
                                projectId,
                                calculationId,
                                reversedState.meta!
                            ).pipe(
                                map(result => CalculationActions.updateMetaSuccess({
                                    projectId,
                                    calculationId,
                                    meta: result
                                })),
                                catchError(error =>
                                    of(CalculationActions.updateMetaFailure({ error }))
                                )
                            )
                        );
                        break;

                    case 'lines':
                        entityActions.push(
                            ...(actions.lines ?? []).map(m => {
                                switch (m.action) {
                                    case 'ADDED':
                                        return isUndo
                                            ? this.calculationService.bulkDeleteLines$(projectId, calculationId, m.entities).pipe(
                                                map(result => CalculationActions.bulkUpdateLinesSuccess({
                                                    lines: m.entities
                                                })),
                                                catchError(error => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                                            )
                                            : this.calculationService.bulkUndeleteLines$(projectId, calculationId, m.entities).pipe(
                                                map(result => CalculationActions.bulkUpdateLinesSuccess({
                                                    lines: m.entities
                                                })),
                                                catchError(error => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                                            );

                                    case 'REMOVED':
                                        return isUndo
                                            ? this.calculationService.bulkUndeleteLines$(projectId, calculationId, m.entities).pipe(
                                                map(result => CalculationActions.bulkUpdateLinesSuccess({
                                                    lines: m.entities
                                                })),
                                                catchError(error => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                                            )
                                            : this.calculationService.bulkDeleteLines$(projectId, calculationId, m.entities).pipe(
                                                map(result => CalculationActions.bulkUpdateLinesSuccess({
                                                    lines: m.entities
                                                })),
                                                catchError(error => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                                            );

                                    case 'UPDATED':
                                        return this.calculationService.bulkUpdateLines$(projectId, calculationId, m.entities).pipe(
                                            map(result => CalculationActions.bulkUpdateLinesSuccess({
                                                lines: result
                                            })),
                                            catchError(error => of(CalculationActions.bulkUpdateLinesFailure({ error })))
                                        );

                                    default: return of(CalculationActions.bulkUpdateLinesFailure({ error: new Error(`Unknown action type: ${m.action}`) }))
                                }
                            })
                        );
                        break;
                }
            }

            return entityActions;
        } catch (error) {
            return [
                of(CalculationActions.calculationUndoRedoFailure({ error }))
            ];
        }
    }

    private groupUndoActions(diffs: StaleProperty[], reversedState: CalculationState): UndoActions {
        const result: UndoActions = {};

        // Update all diffs with a path that is more than one level deep
        // We convert the action on the sub object to a 'UPDATED' action on the root object
        // For the backend, only root objects are valid entities in the data model
        const updatedDiffs = diffs.map<StaleProperty & { rootObject?: any }>(m => {
            const pathParts = m.path?.split('.').filter(Boolean) ?? [];
            const convertToRoot = pathParts.length > 1;
            let rootObject = undefined;

            if (convertToRoot) {
                const [, rootEntity, id] = /^([^\.\[\]]*)(?:\[(.*)\])?/.exec(m.path) ?? [];

                if (!rootEntity) {
                    throw new Error(`Invalid path ${m.path}`);
                } else {
                    rootObject = reversedState[rootEntity as keyof CalculationState];
                }

                // If id is provided between brackets, the root object should be an array
                // containing objects with property id
                if (id) {
                    if (!Array.isArray(rootObject)) {
                        throw new Error(`Id is provided in path ${m.path}. But entity is not an array`);
                    } else {
                        rootObject = (rootObject as Array<{ id: string }>).find(f => f.id === id);

                        // Check if object was found
                        if (!rootObject) {
                            throw new Error(`Object was not found: ${m.path}`);
                        }
                    }
                }
            }

            return {
                ...m,
                rootObject: rootObject,
                diffType: convertToRoot ? 'UPDATED' : m.diffType // Keep diffType if not deeper than one level
            }
        })

        // console.log('UPDATED DIFFS', updatedDiffs)

        // Get all keys of CalculationState that are in the diffs
        const distinctEntities = [...new Set(updatedDiffs.map(d => d.path.split(/[\.\[\]]/).filter(Boolean).shift() as keyof CalculationState))];

        // console.log('DISTINCT', distinctEntities);

        for (const entity of distinctEntities) {
            const entityActions: EntityAction[] = [];

            // Get distinct actions for this entity
            const diffTypes = [...new Set(updatedDiffs.map(m => m.diffType))];

            for (const diffType of diffTypes) {
                // Get the current diffs
                const instances = updatedDiffs.filter(f => f.diffType === diffType)
                    .map(m => {
                        switch (diffType) {
                            case 'ADDED':
                                return m.newValue;
                            case 'REMOVED':
                                return m.oldValue;
                            case 'UPDATED':
                                return m.rootObject;
                        }
                    });

                entityActions.push({
                    action: diffType,
                    entities: instances
                })
            }

            result[entity] = entityActions;
        }

        return result;
    }

    // Hourly rates ============================================
    addHourlyRate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeaderActions.addHeaderLine),
            mergeMap((action) =>
                this.calculationService.addHourlyRate$(action.projectId, action.calculationId, action.rate).pipe(
                    map((rate) =>
                        HeaderActions.addHeaderLineSuccess({
                            rate
                        })
                    ),
                    catchError(error =>
                        of(HeaderActions.addHeaderLineFailure({ error }))
                    )
                )
            )
        )
    );

    removeHourlyRate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HeaderActions.deleteHeaderLine),
            withLatestFrom(this.store.select(selectLines)),
            concatMap(([action, lines]) => {
                const allAffectedLines = lines.filter(l => l.rate === action.rate.id);

                const updateAffectedLines$ = allAffectedLines.length > 0
                    ? this.calculationService.bulkUpdateLines$(
                        action.projectId,
                        action.calculationId,
                        allAffectedLines.map(l => ({
                            ...l,
                            rate: '00000000-0000-0000-0000-000000000000',
                        }))
                    ).pipe(
                        map(() => CalculationActions.loadCalculation({
                            projectId: action.projectId,
                            calculationId: action.calculationId,
                        }))
                    )
                    : EMPTY;

                const deleteHeaderLine$ = this.calculationService.deleteHeaderLine$(
                    action.projectId,
                    action.calculationId,
                    action.rate
                ).pipe(
                    map(rate => HeaderActions.addHeaderLineSuccess({ rate }))
                );

                return concat(
                    updateAffectedLines$,
                    deleteHeaderLine$
                ).pipe(
                    catchError(error =>
                        of(HeaderActions.addHeaderLineFailure({ error }))
                    )
                );
            })
        )
    );

    pdfExport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.pdfExport),
            withLatestFrom(this.store.select(selectCalculationState)),
            mergeMap(([action, state]) =>
                from(this.pdfExport.createPdf(createViewModel(state, undefined, action.options.language)?.viewModelLines, state.meta!, action.options)).pipe(
                    map((file) => CalculationActions.pdfExportCreationSuccess({ file, options: action.options })),
                    catchError((error) => of(CalculationActions.pdfExportFailure({ error })))
                )
            )
        )
    );

    pdfExportSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CalculationActions.pdfExportCreationSuccess),
            withLatestFrom(this.store.select(selectCalculationMeta)),
            mergeMap(([action, meta]) =>
                from(this.projectCalculationService.getProjectById(meta?.projectId!)).pipe(
                    mergeMap(project => {
                        // Create file name
                        // const fileName = `${[project.id ?? '', project.description ?? 'export'].filter(f => !!f).join(' ')}.pdf`; 

                        const nameParts = [
                            meta?.projectId,
                            '-',
                            meta?.description,
                            '-',
                            action.options.title[action.options?.language ?? 'nl'],
                            meta?.displayVersion || meta?.currentVersion ? `v${meta?.displayVersion || meta?.currentVersion}` : null,
                            meta?.displayDate || meta?.versionDate ? new Date(meta?.displayDate! || meta?.versionDate!).toISOString().split('T')[0] : undefined
                        ]

                        const fileName = nameParts.filter(part => !!part).join(' ') + '.pdf';

                        saveAs(new Blob([action.file], { type: 'application/pdf' }), fileName);

                        return of(CalculationActions.pdfExportDownloadSuccess())
                    }),
                    catchError(error =>
                        of(CalculationActions.pdfExportFailure({ error }))
                    )
                )
            ),
        )
    )

    startScrolling$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CalculationActions.startScrolling), // Filter for start scrolling actions
                tap(() => {
                    const activeElement = document.activeElement as HTMLElement;
                    if (activeElement) {
                        activeElement.blur(); // Make sure the active element loses focus
                        // console.log('Focus removed from:', activeElement);
                    }
                })
            ),
        { dispatch: false } // No action is dispatched
    );

    // loadData$ = createEffect(() =>
    //     this.actions$.pipe(
    //         ofType(loadData),
    //         concatMap(action => {
    //             if (someCondition(action)) {
    //                 return this.backendService.getData1().pipe(
    //                     map(response1 => loadData1Success({ data: response1 })),
    //                     catchError(error => of(loadDataFailure({ error })))
    //                 );
    //             } else {
    //                 // Geen actie nodig als de eerste call niet is uitgevoerd
    //                 return of(null);
    //             }
    //         }),
    //         concatMap(() =>
    //             this.backendService.getData2().pipe(
    //                 map(response2 => loadData2Success({ data: response2 })),
    //                 catchError(error => of(loadDataFailure({ error })))
    //             )
    //         )
    //     )
    // );

    constructor(
        private actions$: Actions,
        private calculationService: DataService,
        private router: Router,
        private store: Store<AppState>,
        private pdfExport: PdfExportService,
        private projectCalculationService: ProjectCalculationService,
    ) {
    }
}
